import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemTagIcon} from '../../../../components/common/dropdowns/dropdownItemTagIcon';
import {
  maybeRenderDeduplicationIcon,
  renderFullTagName,
  renderTagNameInTooltip,
} from '../../../../components/common/dropdowns/tagDropdownItem';
import {ResourceMultiSelectFilter} from '../../../../components/common/filters/resourceMultiSelectFilter';
import {FilterRowGenerator} from '../../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../../../components/common/filters/sourceFilter/filterDropdownSource';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SearchTagModel} from '../../../../models/api/searchTagModel';
import {TagModel} from '../../../../models/api/tagModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getStarredTagIds} from '../../../../store/data/tags/tagsSelectors';
import {getSelectedTeamNamespaces} from '../../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {getAnalyticsSelectedNamespace} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {makeTagsFetcher} from '../../../helpers/analyticsFiltersHelpers';

/*
 * Props.
 */

interface TagsSelectionDropdownProps {
  name: ReactNode;
  tagsMap: ResourceMap<TagModel>;
  setTagsMap: (arg: ResourceMap<TagModel>) => void;
  selectedTags: ReadonlyArray<number>;
  onSetTags: (arg: ReadonlyArray<number>) => void;
  onClearAll: () => void;
  renderButton: Renderer;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

/*
 * Component.
 */

export const TagsSelectionDropdown: FC<TagsSelectionDropdownProps> = ({
  name,
  tagsMap,
  setTagsMap,
  selectedTags,
  onSetTags,
  onClearAll,
  renderButton,
}) => {
  const company = useAppSelector(getCompany);
  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const selectedTeamNamespaces = useAppSelector(getSelectedTeamNamespaces);
  const starredTagIds = useAppSelector(getStarredTagIds);

  /*
   * Fetcher.
   */

  const fetcher = useMemo(
    () => makeTagsFetcher(company, namespace, selectedTeamNamespaces),
    [company, namespace, selectedTeamNamespaces],
  );

  /*
   * Tags selection dropdown.
   */

  const dropdownSource = computeFilterDropdownSourceAsync(fetcher);

  const computeFilterGenerator: FilterRowGenerator<SearchTagModel> = (
    item,
    dropdownSelectedIds,
    makeOnItemSelect,
    isSearching,
  ) => {
    // Tag search returns an array of tags. The array represents the tag and its parent tags,
    // with the child tag coming first in the array. The request returns every tag, regardless
    // of its place in the hierarchy.
    const tag = item[0];

    const isChecked = dropdownSelectedIds.includes(tag.id);
    const onItemSelect = makeOnItemSelect(tag.id);
    const onSelect = () => {
      onItemSelect();
      // Add this item to our tag map so we have it for later rendering.
      setTagsMap({...tagsMap, [tag.id]: tag});
    };

    const nestingLevel = isSearching ? 0 : item.length - 1;
    // If we are searching we display the tag + it's path on the same line,
    // and when not searching we display a tree nested view.
    const tagsPath = isSearching ? item : [];
    return {
      renderer: () => (
        <>
          <DropdownItemTagIcon tag={tag} nestingLevel={nestingLevel} />
          <DropdownItemContent isChecked={isChecked}>
            <StyledContentDiv>
              {renderFullTagName(tag, tagsPath, starredTagIds.has(tag.id))}
              {maybeRenderDeduplicationIcon(tag)}
            </StyledContentDiv>
          </DropdownItemContent>
        </>
      ),
      onSelect,
      isSelected: isChecked,
      renderTooltip: () => renderTagNameInTooltip(tag, tagsPath),
    };
  };

  return (
    <ResourceMultiSelectFilter
      dropdownTitle={name}
      selectedItemIds={selectedTags}
      onSetItems={onSetTags}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={onClearAll}
      renderButtonValue={renderButton}
    />
  );
};
